import Button from 'react-bootstrap/Button';
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import config from "../../config";
import stateContext from '../../data/state_context';
import ConcreteEmissionType from "./ConcreteEmissionType";
import axios from "axios";
import "./ConcreteEmission.css";
import { Col, Container, Form, Modal, Row, ToggleButton, ButtonGroup } from 'react-bootstrap';
import SideNav from '../SideNav/SideNav';
import HelpIcon from '../HelpIcon/HelpIcon';
import { useStateContext } from '../../context/StateContext';
import useFormValidation from '../../hooks/useFormValidation';
import ValidationError from '../ValidationError/ValidationError';

const ConcreteEmission = () => {
    let { id } = useParams();
    const { selectedState, setSelectedState, isStateSelected } = useStateContext();
    const { handleBlur, getFieldState, validateAll } = useFormValidation();
    const [strength, setStrength] = useState(4000);
    const [data, setData] = useState(ConcreteEmissionType);
    const [unitSystem, setUnitSystem] = useState('metric'); // 'metric' or 'imperial'
    const [emissionsUnit, setEmissionsUnit] = useState('kgCO2eq_m3'); // 'kgCO2eq_m3' or 'kgCO2eq_yd3'

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const backref = queryParams.get('backref');

    const [error, setError] = useState(null);
    const [errorPopupShow, setErrorPopupShow] = useState(false);
    const [stateWarningShow, setStateWarningShow] = useState(false);
    const [validationErrorShow, setValidationErrorShow] = useState(false);

    let navigate = useNavigate();

    const supportedStrengths = [2500, 3000, 4000, 5000, 6000, 8000];

    const conversionFactors = {
        // volume conversions
        m3_to_yd3: 1.30795,
        m3_to_ft3: 35.3147,

        // mass conversions
        kg_to_lb: 2.20462, // for regular kg to lb conversions
        tnsh_to_lb: 2000,  // for short tons to pounds

        // density conversions
        kgm3_to_lbyd3: 1.68555, // used for density, emissions, and cost

        // energy conversions
        kwh_to_btu: 3412.14,

        // liquid volume conversions
        lit_to_gal: 0.264172,

        kgCO2eq_m3_to_kgCO2eq_yd3: 0.764555
    };

    const toggleUnitSystem = () => {
        setUnitSystem(prev => prev === 'metric' ? 'imperial' : 'metric');
    };

    const toggleEmissionsUnit = () => {
        setEmissionsUnit(prev => prev === 'kgCO2eq_m3' ? 'kgCO2eq_yd3' : 'kgCO2eq_m3');
    };

    // helper function to display values with appropriate units
    const displayValue = (value, unitType) => {
    if (unitType !== 'emissions') {
        // Handle non-emissions units as before
        if (unitSystem === 'metric') {
            return value;
        }

    switch(unitType) {
        case 'volume':
            return value * conversionFactors.m3_to_yd3;
        case 'density':
        case 'cost':
            return value * conversionFactors.kgm3_to_lbyd3;
        case 'weight':
            return value * conversionFactors.kg_to_lb;
        case 'solid':
            return value * conversionFactors.tnsh_to_lb;
        case 'energy':
            return value * conversionFactors.kwh_to_btu;
        case 'smallVolume':
        case 'gasVolume':
            return value * conversionFactors.m3_to_ft3;
        case 'liquid':
            return value * conversionFactors.lit_to_gal;
        case 'percentage':
            return value;
        default:
            return value;
        }
    }

  // handle emissions units specifically
  if (unitSystem === 'metric') {
    // Metric system - show either kg/m³ or kg/yd³
    return emissionsUnit === 'kgCO2eq_m3'
        ? value
        : value * conversionFactors.kgCO2eq_m3_to_kgCO2eq_yd3;
    } else {
        // imperial system - show either lb/yd³ or kg/yd³
        if (emissionsUnit === 'kgCO2eq_yd3') {
        // show kg/yd³ - convert from kg/m³ to kg/yd³
        return value * conversionFactors.kgCO2eq_m3_to_kgCO2eq_yd3;
        } else {
        // show lb/yd³ - convert from kg/m³ to lb/yd³
        return value * conversionFactors.kgm3_to_lbyd3;
        }
    }
};

    // Convert from display units back to storage units (metric)
    const inverseDisplayValue = (value, unitType) => {
        if (unitSystem === 'metric') {
            return value; // No conversion needed
        }

        // Convert from imperial back to metric
        switch(unitType) {
            case 'volume':
                return value / conversionFactors.m3_to_yd3;
            case 'density':
            case 'cost':
                return value / conversionFactors.kgm3_to_lbyd3;
            case 'weight':
                return value / conversionFactors.kg_to_lb;
            case 'solid':
                return value / conversionFactors.tnsh_to_lb;
            case 'energy':
                return value / conversionFactors.kwh_to_btu;
            case 'smallVolume':
            case 'gasVolume':
                return value / conversionFactors.m3_to_ft3;
            case 'liquid':
                return value / conversionFactors.lit_to_gal;
            case 'percentage':
            default:
                return value;
        }
    };

    // helper function to display unit labels
    const displayUnit = (metricUnit, imperialUnit) => {
        return unitSystem === 'metric' ? metricUnit : imperialUnit;
    };

    const displayEmissionsUnit = () => {
    if (unitSystem === 'metric') {
        return emissionsUnit === 'kgCO2eq_m3' ? 'kg CO₂ eq/m³' : 'kg CO₂ eq/yd³';
    } else {
        return emissionsUnit === 'kgCO2eq_m3' ? 'lb CO₂ eq/yd³' : 'kg CO₂ eq/yd³';
    }
    };

    const getTotalMaterialEmission = (data) => {
        let result = Number(data.material.portlandCement || 0) * Number(data.factor.material.portlandCement || 0) +
        Number(data.material.flyAsh || 0) * Number(data.factor.material.flyAsh || 0) +
        Number(data.material.slagCement || 0) * Number(data.factor.material.slagCement || 0) +
        Number(data.material.mixingWater || 0) * Number(data.factor.material.mixingWater || 0) +
        Number(data.material.crushedCoarseAggregate || 0) * Number(data.factor.material.crushedCoarseAggregate || 0) +
        Number(data.material.naturalCoarseAggregate || 0) * Number(data.factor.material.naturalCoarseAggregate || 0) +
        Number(data.material.crushedFineAggregate || 0) * Number(data.factor.material.crushedFineAggregate || 0) +
        Number(data.material.naturalFineAggregate || 0) * Number(data.factor.material.naturalFineAggregate || 0) +
        Number(data.material.air || 0) * Number(data.factor.material.air || 0) +
        Number(data.material.airEntrainingMixture || 0) * Number(data.factor.material.airEntrainingMixture || 0) +
        Number(data.material.waterReducer || 0) * Number(data.factor.material.waterReducer || 0) +
        Number(data.material.highRangeWaterReducer || 0) * Number(data.factor.material.highRangeWaterReducer || 0) +
        Number(data.material.accelerator || 0) * Number(data.factor.material.accelerator || 0);
        return result;
    };

    const getTotalEnergyEmission = (data) => {
        let result = Number(data.energy.purchasedElectricity || 0) * Number(data.factor.energy.purchasedElectricity || 0 )+
        Number(data.energy.naturalGas || 0) * Number(data.factor.energy.naturalGas || 0 ) +
        Number(data.energy.secondaryFuelLiquid || 0) * Number(data.factor.energy.secondaryFuelLiquid || 0 ) +
        Number(data.energy.secondaryFuelSolid || 0) * Number(data.factor.energy.secondaryFuelSolid || 0 ) +
        Number(data.energy.fuelOil || 0) * Number(data.factor.energy.fuelOil || 0 ) +
        Number(data.energy.diesel || 0) * Number(data.factor.energy.diesel || 0 ) +
        Number(data.energy.gasoline || 0) * Number(data.factor.energy.gasoline || 0 ) +
        Number(data.energy.lpg || 0) * Number(data.factor.energy.lpg || 0 );
        return result;
    }

    const getTotalTransportationEmission = (data) => {
        let result = data.transportation.transportationGHG =
            ((Number(data.transportationMatrix.truck[0] || 0) * Number(data.material.portlandCement || 0) +
            Number(data.transportationMatrix.truck[1] || 0) * Number(data.material.flyAsh || 0) +
            Number(data.transportationMatrix.truck[2] || 0) * Number(data.material.slagCement || 0) +
            Number(data.transportationMatrix.truck[3] || 0) * Number(data.material.crushedCoarseAggregate || 0) +
            Number(data.transportationMatrix.truck[4] || 0) * Number(data.material.naturalCoarseAggregate || 0) +
            Number(data.transportationMatrix.truck[5] || 0) * Number(data.material.crushedFineAggregate || 0) +
            Number(data.transportationMatrix.truck[6] || 0) * Number(data.material.naturalFineAggregate || 0))* Number(data.factor.transportation.truck || 0) +


            (Number(data.transportationMatrix.rail[0] || 0) * Number(data.material.portlandCement || 0) +
            Number(data.transportationMatrix.rail[1] || 0) * Number(data.material.flyAsh || 0) +
            Number(data.transportationMatrix.rail[2] || 0) * Number(data.material.slagCement || 0) +
            Number(data.transportationMatrix.rail[3] || 0) * Number(data.material.crushedCoarseAggregate || 0) +
            Number(data.transportationMatrix.rail[4] || 0) * Number(data.material.naturalCoarseAggregate || 0) +
            Number(data.transportationMatrix.rail[5] || 0) * Number(data.material.crushedFineAggregate || 0) +
            Number(data.transportationMatrix.rail[6] || 0) * Number(data.material.naturalFineAggregate || 0)) * Number(data.factor.transportation.rail || 0) +

            (Number(data.transportationMatrix.ocean[0] || 0) * Number(data.material.portlandCement || 0) +
            Number(data.transportationMatrix.ocean[1] || 0) * Number(data.material.flyAsh || 0) +
            Number(data.transportationMatrix.ocean[2] || 0) * Number(data.material.slagCement || 0) +
            Number(data.transportationMatrix.ocean[3] || 0) * Number(data.material.crushedCoarseAggregate || 0) +
            Number(data.transportationMatrix.ocean[4] || 0) * Number(data.material.naturalCoarseAggregate || 0) +
            Number(data.transportationMatrix.ocean[5] || 0) * Number(data.material.crushedFineAggregate || 0) +
            Number(data.transportationMatrix.ocean[6] || 0) * Number(data.material.naturalFineAggregate || 0)) * Number(data.factor.transportation.ocean || 0) +

            (Number(data.transportationMatrix.barge[0] || 0) * Number(data.material.portlandCement || 0) +
            Number(data.transportationMatrix.barge[1] || 0) * Number(data.material.flyAsh || 0) +
            Number(data.transportationMatrix.barge[2] || 0) * Number(data.material.slagCement || 0) +
            Number(data.transportationMatrix.barge[3] || 0) * Number(data.material.crushedCoarseAggregate || 0) +
            Number(data.transportationMatrix.barge[4] || 0) * Number(data.material.naturalCoarseAggregate || 0) +
            Number(data.transportationMatrix.barge[5] || 0) * Number(data.material.crushedFineAggregate || 0) +
            Number(data.transportationMatrix.barge[6] || 0) * Number(data.material.naturalFineAggregate || 0))* Number(data.factor.transportation.barge || 0)) / 1000;
        return result;
    }

    useEffect(() => {
        axios.get(`${config.apiUrl}/api/emission/concrete/${id}`)
            .then(response => {
                setSelectedState(response.data.state);
                setStrength(response.data.pccStrength);

                response.data.material.total = getTotalMaterialEmission(response.data);
                response.data.energy.total = getTotalEnergyEmission(response.data);
                response.data.transportation.total = getTotalTransportationEmission(response.data);
                response.data.total = (response.data.energy.total + response.data.material.total + response.data.transportation.total);
                setData(response.data);
            })
            .catch(error => {
                console.log(error);
            });
    }, [id]);

    const handleStateChange = (event) => {
        const state = event.target.value;
        setSelectedState(state);

        axios.get(`${config.apiUrl}/api/emission/concrete/state/${state}/${strength}`)
            .then(response => {
                response.data.material.total = getTotalMaterialEmission(response.data);
                response.data.energy.total = getTotalEnergyEmission(response.data);
                response.data.transportation.total = getTotalTransportationEmission(response.data);
                response.data.total = (response.data.energy.total + response.data.material.total + response.data.transportation.total);
                setData(response.data);
        }).catch(error => {
            console.log(error);
        });
    };

    const handleStrenghtChange = (event) => {
        const strength = event.target.value;
        setStrength(strength);

        axios.get(`${config.apiUrl}/api/emission/concrete/state/${selectedState}/${strength}`).then(response => {
            response.data.material.total = getTotalMaterialEmission(response.data);
            response.data.energy.total = getTotalEnergyEmission(response.data);
            response.data.transportation.total = getTotalTransportationEmission(response.data);
            response.data.total = (response.data.energy.total + response.data.material.total + response.data.transportation.total);
            setData(response.data);
        }).catch(error => {
            console.log(error);
        });
    };

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        const unitType = e.target.dataset.unitType; // Get unit type from data attribute
        const nameParts = name.split('.');  // Split the name by dots to handle nested keys, e.g., 'user.name'

        // Convert from display units to storage units (metric) if needed
        let storageValue = value;
        if (unitType && type === 'number' && value !== '') {
            storageValue = inverseDisplayValue(parseFloat(value), unitType);
        }

        setData(prevFormData => {
            // Function to recursively update nested objects
            const updateNestedObject = (obj, parts, val) => {
                const part = parts.shift();  // Get the next property name in the path
                if (parts.length > 0) {
                    obj[part] = updateNestedObject(obj[part] || {}, parts, val);
                } else {
                    obj[part] = type === 'checkbox' ? checked : val;  // Set the value for the last part of the path
                }
                return { ...obj };
            };

            // Update the form data with the new value (converted to metric if needed)
            const updatedFormData = updateNestedObject({ ...prevFormData }, nameParts, storageValue);

            // Calculate the totals
            updatedFormData.material.total =
                (updatedFormData.material.portlandCement || 0) * (updatedFormData.factor.material.portlandCement || 0) +
                (updatedFormData.material.flyAsh || 0) * (updatedFormData.factor.material.flyAsh || 0) +
                (updatedFormData.material.slagCement || 0) * (updatedFormData.factor.material.slagCement || 0) +
                (updatedFormData.material.mixingWater || 0) * (updatedFormData.factor.material.mixingWater || 0) +
                (updatedFormData.material.crushedCoarseAggregate || 0) * (updatedFormData.factor.material.crushedCoarseAggregate || 0) +
                (updatedFormData.material.naturalCoarseAggregate || 0) * (updatedFormData.factor.material.naturalCoarseAggregate || 0) +
                (updatedFormData.material.crushedFineAggregate || 0) * (updatedFormData.factor.material.crushedFineAggregate || 0) +
                (updatedFormData.material.naturalFineAggregate || 0) * (updatedFormData.factor.material.naturalFineAggregate || 0) +
                (updatedFormData.material.air || 0) * (updatedFormData.factor.material.air || 0) +
                (updatedFormData.material.airEntrainingMixture || 0) * (updatedFormData.factor.material.airEntrainingMixture || 0) +
                (updatedFormData.material.waterReducer || 0) * (updatedFormData.factor.material.waterReducer || 0) +
                (updatedFormData.material.highRangeWaterReducer || 0) * (updatedFormData.factor.material.highRangeWaterReducer || 0) +
                (updatedFormData.material.accelerator || 0) * (updatedFormData.factor.material.accelerator || 0);

            updatedFormData.transportation.transportationGHG = (((updatedFormData.transportationMatrix.truck[0] || 0) * (data.material.portlandCement || 0) +
                (updatedFormData.transportationMatrix.truck[1] || 0) * (updatedFormData.material.flyAsh || 0) +
                (updatedFormData.transportationMatrix.truck[2] || 0) * (updatedFormData.material.slagCement || 0) +
                (updatedFormData.transportationMatrix.truck[3] || 0) * (updatedFormData.material.crushedCoarseAggregate || 0) +
                (updatedFormData.transportationMatrix.truck[4] || 0) * (updatedFormData.material.naturalCoarseAggregate || 0) +
                (updatedFormData.transportationMatrix.truck[5] || 0) * (updatedFormData.material.crushedFineAggregate || 0) +
                (updatedFormData.transportationMatrix.truck[6] || 0) * (updatedFormData.material.naturalFineAggregate || 0)) * (updatedFormData.factor.transportation.truck || 0) +


                ((updatedFormData.transportationMatrix.rail[0] || 0) * (updatedFormData.material.portlandCement || 0) +
                    (updatedFormData.transportationMatrix.rail[1] || 0) * (updatedFormData.material.flyAsh || 0) +
                    (updatedFormData.transportationMatrix.rail[2] || 0) * (updatedFormData.material.slagCement || 0) +
                    (updatedFormData.transportationMatrix.rail[3] || 0) * (updatedFormData.material.crushedCoarseAggregate || 0) +
                    (updatedFormData.transportationMatrix.rail[4] || 0) * (updatedFormData.material.naturalCoarseAggregate || 0) +
                    (updatedFormData.transportationMatrix.rail[5] || 0) * (updatedFormData.material.crushedFineAggregate || 0) +
                    (updatedFormData.transportationMatrix.rail[6] || 0) * (updatedFormData.material.naturalFineAggregate || 0)) * (updatedFormData.factor.transportation.rail || 0) +

                ((updatedFormData.transportationMatrix.ocean[0] || 0) * (updatedFormData.material.portlandCement || 0) +
                    (updatedFormData.transportationMatrix.ocean[1] || 0) * (updatedFormData.material.flyAsh || 0) +
                    (updatedFormData.transportationMatrix.ocean[2] || 0) * (updatedFormData.material.slagCement || 0) +
                    (updatedFormData.transportationMatrix.ocean[3] || 0) * (updatedFormData.material.crushedCoarseAggregate || 0) +
                    (updatedFormData.transportationMatrix.ocean[4] || 0) * (updatedFormData.material.naturalCoarseAggregate || 0) +
                    (updatedFormData.transportationMatrix.ocean[5] || 0) * (updatedFormData.material.crushedFineAggregate || 0) +
                    (updatedFormData.transportationMatrix.ocean[6] || 0) * (updatedFormData.material.naturalFineAggregate || 0)) * (updatedFormData.factor.transportation.ocean || 0) +

                ((updatedFormData.transportationMatrix.barge[0] || 0) * (updatedFormData.material.portlandCement || 0) +
                    (updatedFormData.transportationMatrix.barge[1] || 0) * (updatedFormData.material.flyAsh || 0) +
                    (updatedFormData.transportationMatrix.barge[2] || 0) * (updatedFormData.material.slagCement || 0) +
                    (updatedFormData.transportationMatrix.barge[3] || 0) * (updatedFormData.material.crushedCoarseAggregate || 0) +
                    (updatedFormData.transportationMatrix.barge[4] || 0) * (updatedFormData.material.naturalCoarseAggregate || 0) +
                    (updatedFormData.transportationMatrix.barge[5] || 0) * (updatedFormData.material.crushedFineAggregate || 0) +
                    (updatedFormData.transportationMatrix.barge[6] || 0) * (updatedFormData.material.naturalFineAggregate || 0)) * (updatedFormData.factor.transportation.barge || 0)) / 1000;


            updatedFormData.transportation.total = updatedFormData.transportation.transportationGHG

            updatedFormData.energy.total =
                (updatedFormData.energy.purchasedElectricity || 0) * (updatedFormData.factor.energy.purchasedElectricity || 0) +
                (updatedFormData.energy.naturalGas || 0) * (updatedFormData.factor.energy.naturalGas || 0) +
                (updatedFormData.energy.secondaryFuelLiquid || 0) * (updatedFormData.factor.energy.secondaryFuelLiquid || 0) +
                (updatedFormData.energy.secondaryFuelSolid || 0) * (updatedFormData.factor.energy.secondaryFuelSolid || 0) +
                (updatedFormData.energy.fuelOil || 0) * (updatedFormData.factor.energy.fuelOil || 0) +
                (updatedFormData.energy.diesel || 0) * (updatedFormData.factor.energy.diesel || 0) +
                (updatedFormData.energy.gasoline || 0) * (updatedFormData.factor.energy.gasoline || 0) +
                (updatedFormData.energy.lpg || 0) * (updatedFormData.factor.energy.lpg || 0);

            updatedFormData.total =
                (updatedFormData.material.total || 0) +
                (updatedFormData.energy.total || 0) +
                (updatedFormData.transportation.total || 0);

            return updatedFormData;
        });
    }

    // Validate all form fields before submission
    const validateForm = () => {
        const fields = [
            { name: 'material.portlandCement', value: data.material.portlandCement, type: 'numberGteZero' },
            { name: 'material.flyAsh', value: data.material.flyAsh, type: 'numberGteZero' },
            { name: 'material.slagCement', value: data.material.slagCement, type: 'numberGteZero' },
            { name: 'material.mixingWater', value: data.material.mixingWater, type: 'numberGteZero' },
            { name: 'material.crushedCoarseAggregate', value: data.material.crushedCoarseAggregate, type: 'numberGteZero' },
            { name: 'material.naturalCoarseAggregate', value: data.material.naturalCoarseAggregate, type: 'numberGteZero' },
            { name: 'material.crushedFineAggregate', value: data.material.crushedFineAggregate, type: 'numberGteZero' },
            { name: 'material.naturalFineAggregate', value: data.material.naturalFineAggregate, type: 'numberGteZero' },
            { name: 'material.air', value: data.material.air, type: 'numberGteZero' },
            { name: 'material.airEntrainingMixture', value: displayValue(Number(data.material.airEntrainingMixture ?? 0), 'density'), type: 'numberGteZero' },
            { name: 'material.waterReducer', value: displayValue(Number(data.material.waterReducer ?? 0), 'density'), type: 'numberGteZero' },
            { name: 'material.highRangeWaterReducer', value: displayValue(Number(data.material.highRangeWaterReducer ?? 0), 'density'), type: 'numberGteZero' },
            { name: 'material.accelerator', value: displayValue(Number(data.material.accelerator ?? 0), 'density'), type: 'numberGteZero' },
            { name: 'transportation.transportationGHG', value: data.transportation.transportationGHG, type: 'number' },
            { name: 'energy.purchasedElectricity', value: data.energy.purchasedElectricity, type: 'numberGteZero' },
            { name: 'energy.naturalGas', value: data.energy.naturalGas, type: 'numberGteZero' },
            { name: 'energy.secondaryFuelLiquid', value: data.energy.secondaryFuelLiquid, type: 'numberGteZero' },
            { name: 'energy.secondaryFuelSolid', value: data.energy.secondaryFuelSolid, type: 'numberGteZero' },
            { name: 'energy.fuelOil', value: data.energy.fuelOil, type: 'numberGteZero' },
            { name: 'energy.diesel', value: data.energy.diesel, type: 'numberGteZero' },
            { name: 'energy.gasoline', value: data.energy.gasoline, type: 'numberGteZero' },
            { name: 'energy.lpg', value: data.energy.lpg, type: 'numberGteZero' },
        ];
        return validateAll(fields);
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        let dataDTO = {"pccStrength": strength, ...data};
        axios.put(`${config.apiUrl}/api/emission/concrete/${id}`, dataDTO).then(response => {
            navigate(`/asphalt-embodied-emission/${id}`);

        }).catch(error => {
        });
    }

    const goBack = () => {
        if (backref) {
            navigate(`/${backref}/${id}`);
        } else {
            navigate(`/pavement-design/${id}`);
        }
    };

    const goToBasicContext = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        let dataDTO = {"pccStrength": strength, ...data};
        axios.put(`${config.apiUrl}/api/emission/concrete/${id}`, dataDTO).then(response => {
            navigate(`/pavement-context-basic/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToDetailedContext = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        let dataDTO = {"pccStrength": strength, ...data};
        axios.put(`${config.apiUrl}/api/emission/concrete/${id}`, dataDTO).then(response => {
            navigate(`/pavement-context-custom/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToPavementDesign = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        let dataDTO = {"pccStrength": strength, ...data};
        axios.put(`${config.apiUrl}/api/emission/concrete/${id}`, dataDTO).then(response => {
            navigate(`/pavement-design/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToPedegreeMatrix = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        let dataDTO = {"pccStrength": strength, ...data};
        axios.put(`${config.apiUrl}/api/emission/concrete/${id}`, dataDTO).then(response => {
            navigate(`/pedegree-matrix/${id}?backref=concrete-embodied-emission`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToAsphaltEmbodiedEmission = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        let dataDTO = {"pccStrength": strength, ...data};
        axios.put(`${config.apiUrl}/api/emission/concrete/${id}`, dataDTO).then(response => {
            navigate(`/asphalt-embodied-emission/${id}?backref=concrete-embodied-emission`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToResults = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        let dataDTO = {"pccStrength": strength, ...data};
        axios.put(`${config.apiUrl}/api/emission/concrete/${id}`, dataDTO).then(response => {
            navigate(`/report/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const handleErrorPopupClose = () => setErrorPopupShow(false);
    const handleErrorPopupShow = () => setErrorPopupShow(true);

    // Handle input blur - convert to number and trigger validation
    const handleInputBlur = (e) => {
        const numericValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
        const validationType = e.target.dataset.validationType || 'number';
        handleChange({ target: { name: e.target.name, value: numericValue, type: 'number', dataset: e.target.dataset } });
        handleBlur(e.target.name, numericValue, validationType);
    };

    return (
        <>
            <SideNav active="concrete-embodied-emission" id={id} passed={["pavement-context-basic", "pavement-context-custom"]}
                goToBasicContext={goToBasicContext}
                goToDetailedContext={goToDetailedContext}
                goToAsphaltEmbodiedEmission={goToAsphaltEmbodiedEmission}
                goToConcreteEmbodiedEmission={() => { }}
                goToPedegreeMatrix={goToPedegreeMatrix}
                goToPavementDesign={goToPavementDesign}
                goToResults={goToResults}
                isStateSelected={isStateSelected}
                onNavigationBlocked={() => setStateWarningShow(true)}
            />
            <Container>
            <Row>
                <Col>
                <div className="pcb__sub">
                    <div className="pcb__box cee__toppanel">
                        <div className="cee__el-row">
                            <div className="cee__label-top">State</div>
                            <Form.Select
                                size="sm"
                                style={{ width: "250px" }} value={selectedState} onChange={handleStateChange}>
                                {!isStateSelected && <option value="">Select a State</option>}
                                {Object.keys(stateContext).map(state => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="cee__el-row">
                            <div className="cee__label-top">PCC Compressive Strength (psi)</div>
                                <Form.Select
                                    size="sm"
                                    style={{ width: "250px" }} value={strength} disabled={!isStateSelected} onChange={handleStrenghtChange}>
                                    {supportedStrengths.map(strength => (
                                        <option key={strength} value={strength}>{strength}</option>
                                    ))}
                            </Form.Select>
                        </div>

                        {/* unit toggle button */}
                        <div className="cee__el-row">
                            <div className="cee__label-top">Unit Toggle</div>
                            <ButtonGroup size="sm">
                                <ToggleButton
                                    id="unit-toggle"
                                    type="checkbox"
                                    variant={unitSystem === 'metric' ? 'primary' : 'outline-primary'}
                                    checked={unitSystem === 'metric'}
                                    onChange={toggleUnitSystem}
                                >
                                    Metric
                                </ToggleButton>
                                <ToggleButton
                                    id="unit-toggle"
                                    type="checkbox"
                                    variant={unitSystem === 'imperial' ? 'primary' : 'outline-primary'}
                                    checked={unitSystem === 'imperial'}
                                    onChange={toggleUnitSystem}
                                >
                                    Imperial
                                </ToggleButton>
                            </ButtonGroup>
                        </div>
                    </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col>

                    <div className="pcb__sub">
                    <h3 className="pcb__sub-title">
                        Materials (A1)
                        <HelpIcon text="Raw material extraction and processing. Includes mining, quarrying, and material production emissions." />
                    </h3>
                    <div className="pcb__box">
                        <table style={{ width: "90%" }}>
                            <tr>
                                <td className="pcc__table-label">Portland Cement</td>
                                <td>
                                <Form.Control
                                    type="number"
                                    size="sm"
                                    step="1"
                                    style={{ width: "95%" }}
                                    name="material.portlandCement"
                                    id="material.portlandCement"
                                    data-unit-type="density"
                                    data-validation-type="numberGteZero"
                                    value={data.material.portlandCement === '' ? '' : parseFloat(displayValue(Number(data.material.portlandCement ?? 0), 'density').toFixed(0))}
                                    disabled={!isStateSelected}
                                    onChange={handleChange}
                                    onBlur={handleInputBlur}
                                    isInvalid={getFieldState('material.portlandCement').isInvalid} />
                                <ValidationError message={getFieldState('material.portlandCement').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Fly Ash</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.flyAsh"
                                        id="material.flyAsh"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.flyAsh === '' ? '' : parseFloat(displayValue(Number(data.material.flyAsh ?? 0), 'density').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.flyAsh').isInvalid} />
                                    <ValidationError message={getFieldState('material.flyAsh').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Slag Cement</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.slagCement"
                                        id="material.slagCement"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.slagCement === '' ? '' : parseFloat(displayValue(Number(data.material.slagCement ?? 0), 'density').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.slagCement').isInvalid} />
                                    <ValidationError message={getFieldState('material.slagCement').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Mixing Water</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.mixingWater"
                                        id="material.mixingWater"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.mixingWater === '' ? '' : parseFloat(displayValue(Number(data.material.mixingWater ?? 0), 'density').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.mixingWater').isInvalid} />
                                    <ValidationError message={getFieldState('material.mixingWater').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Crushed Coarse Aggregate</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.crushedCoarseAggregate"
                                        id="material.crushedCoarseAggregate"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.crushedCoarseAggregate === '' ? '' : parseFloat(displayValue(Number(data.material.crushedCoarseAggregate ?? 0), 'density').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.crushedCoarseAggregate').isInvalid} />
                                    <ValidationError message={getFieldState('material.crushedCoarseAggregate').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Natural Coarse Aggregate</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.naturalCoarseAggregate"
                                        id="material.naturalCoarseAggregate"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.naturalCoarseAggregate === '' ? '' : parseFloat(displayValue(Number(data.material.naturalCoarseAggregate ?? 0), 'density').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.naturalCoarseAggregate').isInvalid} />
                                    <ValidationError message={getFieldState('material.naturalCoarseAggregate').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Crushed Fine Aggregate</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.crushedFineAggregate"
                                        id="material.crushedFineAggregate"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.crushedFineAggregate === '' ? '' : parseFloat(displayValue(Number(data.material.crushedFineAggregate ?? 0), 'density').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.crushedFineAggregate').isInvalid} />
                                    <ValidationError message={getFieldState('material.crushedFineAggregate').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Natural Fine Aggregate</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.naturalFineAggregate"
                                        id="material.naturalFineAggregate"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.naturalFineAggregate === '' ? '' : parseFloat(displayValue(Number(data.material.naturalFineAggregate ?? 0), 'density').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.naturalFineAggregate').isInvalid} />
                                    <ValidationError message={getFieldState('material.naturalFineAggregate').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Air</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="1"
                                        style={{ width: "95%" }}
                                        name="material.air"
                                        id="material.air"
                                        data-unit-type="percentage"
                                        data-validation-type="numberGteZero"
                                        value={data.material.air === '' ? '' : parseFloat(displayValue(Number(data.material.air ?? 0), 'percentage').toFixed(0))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.air').isInvalid} />
                                    <ValidationError message={getFieldState('material.air').errorMessage} />
                                </td>
                                <td className="cee_value">%</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Air Entraining Mixture</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="material.airEntrainingMixture"
                                        id="material.airEntrainingMixture"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.airEntrainingMixture === '' ? '' : parseFloat(displayValue(Number(data.material.airEntrainingMixture ?? 0), 'density').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.airEntrainingMixture').isInvalid} />
                                    <ValidationError message={getFieldState('material.airEntrainingMixture').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Water Reducer</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="material.waterReducer"
                                        id="material.waterReducer"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.waterReducer === '' ? '' : parseFloat(displayValue(Number(data.material.waterReducer ?? 0), 'density').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.waterReducer').isInvalid} />
                                    <ValidationError message={getFieldState('material.waterReducer').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">High Range Water Reducer</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="material.highRangeWaterReducer"
                                        id="material.highRangeWaterReducer"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.highRangeWaterReducer === '' ? '' : parseFloat(displayValue(Number(data.material.highRangeWaterReducer ?? 0), 'density').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.highRangeWaterReducer').isInvalid} />
                                    <ValidationError message={getFieldState('material.highRangeWaterReducer').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Accelerator</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="material.accelerator"
                                        id="material.accelerator"
                                        data-unit-type="density"
                                        data-validation-type="numberGteZero"
                                        value={data.material.accelerator === '' ? '' : parseFloat(displayValue(Number(data.material.accelerator ?? 0), 'density').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('material.accelerator').isInvalid} />
                                    <ValidationError message={getFieldState('material.accelerator').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg/m³', 'lb/yd³')}</td>
                            </tr>

                        </table>
                    </div>
                    </div>

                    <div className="pcb__sub">
                    <h3 className="pcb__sub-title">
                        Transportation (A2)
                        <HelpIcon text="Transport of materials to the manufacturing plant. Based on distance and transport mode." />
                    </h3>
                    <div className="pcb__box">
                        <table style={{ width: "90%" }}>
                            <tr>
                                <td className="pcc__table-label">Transportation Emission</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="transportation.transportationGHG"
                                        id="transportation.transportationGHG"
                                        data-unit-type="emissions"
                                        value={data.transportation.transportationGHG === '' ? '' : parseFloat(displayValue(Number(data.transportation.transportationGHG ?? 0), 'emissions').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('transportation.transportationGHG').isInvalid} />
                                    <ValidationError message={getFieldState('transportation.transportationGHG').errorMessage} />
                                </td>
                                <td className="cee_value">{displayEmissionsUnit()}</td>
                            </tr>
                        </table>
                    </div>
                    </div>


                </Col>
                <Col>


                <div className="pcb__sub">
                    <h3 className="pcb__sub-title">
                        Energy (A3)
                        <HelpIcon text="Energy consumed during manufacturing. Includes electricity, natural gas, and fuel usage at the plant." />
                    </h3>
                    <div className="pcb__box">
                        <table style={{ width: "90%" }}>
                            <tr>
                                <td className="pcc__table-label">Purchased Electricity</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.purchasedElectricity"
                                        id="energy.purchasedElectricity"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.purchasedElectricity === '' ? '' : parseFloat(Number(data.energy.purchasedElectricity ?? 0).toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.purchasedElectricity').isInvalid} />
                                    <ValidationError message={getFieldState('energy.purchasedElectricity').errorMessage} />
                                </td>
                                <td className="cee_value">kWh</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Natural Gas</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.naturalGas"
                                        id="energy.naturalGas"
                                        data-unit-type="gasVolume"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.naturalGas === '' ? '' : parseFloat(displayValue(Number(data.energy.naturalGas ?? 0), 'gasVolume').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.naturalGas').isInvalid} />
                                    <ValidationError message={getFieldState('energy.naturalGas').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('m³', 'ft³')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Secondary Fuels - Liquid</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.secondaryFuelLiquid"
                                        id="energy.secondaryFuelLiquid"
                                        data-unit-type="weight"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.secondaryFuelLiquid === '' ? '' : parseFloat(displayValue(Number(data.energy.secondaryFuelLiquid ?? 0), 'weight').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.secondaryFuelLiquid').isInvalid} />
                                    <ValidationError message={getFieldState('energy.secondaryFuelLiquid').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('kg', 'lb')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Secondary Fuels - Solid</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.secondaryFuelSolid"
                                        id="energy.secondaryFuelSolid"
                                        data-unit-type="solid"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.secondaryFuelSolid === '' ? '' : parseFloat(displayValue(Number(data.energy.secondaryFuelSolid ?? 0), 'solid').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.secondaryFuelSolid').isInvalid} />
                                    <ValidationError message={getFieldState('energy.secondaryFuelSolid').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('tn.sh', 'lb')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Fuel Oil (other than diesel)</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.fuelOil"
                                        id="energy.fuelOil"
                                        data-unit-type="liquid"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.fuelOil === '' ? '' : parseFloat(displayValue(Number(data.energy.fuelOil ?? 0), 'liquid').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.fuelOil').isInvalid} />
                                    <ValidationError message={getFieldState('energy.fuelOil').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('lit', 'gal')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Diesel</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.diesel"
                                        id="energy.diesel"
                                        data-unit-type="liquid"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.diesel === '' ? '' : parseFloat(displayValue(Number(data.energy.diesel ?? 0), 'liquid').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.diesel').isInvalid} />
                                    <ValidationError message={getFieldState('energy.diesel').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('lit', 'gal')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">Gasoline</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.gasoline"
                                        id="energy.gasoline"
                                        data-unit-type="liquid"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.gasoline === '' ? '' : parseFloat(displayValue(Number(data.energy.gasoline ?? 0), 'liquid').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.gasoline').isInvalid} />
                                    <ValidationError message={getFieldState('energy.gasoline').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('lit', 'gal')}</td>
                            </tr>

                            <tr>
                                <td className="pcc__table-label">LPG</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "95%" }}
                                        name="energy.lpg"
                                        id="energy.lpg"
                                        data-unit-type="liquid"
                                        data-validation-type="numberGteZero"
                                        value={data.energy.lpg === '' ? '' : parseFloat(displayValue(Number(data.energy.lpg ?? 0), 'liquid').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={handleInputBlur}
                                        isInvalid={getFieldState('energy.lpg').isInvalid} />
                                    <ValidationError message={getFieldState('energy.lpg').errorMessage} />
                                </td>
                                <td className="cee_value">{displayUnit('lit', 'gal')}</td>
                            </tr>
                        </table>
                    </div>
                    </div>



                    <div className="pcb__sub">
                    <h3 className="pcb__sub-title">Total Emissions (A1+A2+A3)</h3>
                    <div className="pcb__box">
                        <table style={{ width: "90%" }}>
                            <tr>
                                <th>Material (A1)</th>
                                <th>Transportation (A2)</th>
                                <th>Energy (A3)</th>

                            </tr>
                            <tr>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.0001"
                                        style={{ width: "95%" }}
                                        name="material.total"
                                        id="material.total"
                                        disabled={!isStateSelected}
                                        value={parseFloat(displayValue(Number(data.material.total ?? 0), 'emissions').toFixed(2))} />
                                </td>

                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.0001"
                                        style={{ width: "95%" }}
                                        name="transportation.total"
                                        id="transportation.total"
                                        disabled={!isStateSelected}
                                        value={parseFloat(displayValue(Number(data.transportation.total ?? 0), 'emissions').toFixed(2))} />
                                </td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.0001"
                                        style={{ width: "95%" }}
                                        name="energy.total"
                                        id="energy.total"
                                        disabled={!isStateSelected}
                                        value={parseFloat(displayValue(Number(data.energy.total ?? 0), 'emissions').toFixed(2))} />
                                </td>
                            </tr>
                            <tr>
                                <td>Total Emissions</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.0001"
                                        style={{ width: "95%" }}
                                        name="total"
                                        id="total"
                                        disabled={!isStateSelected}
                                        value={parseFloat(displayValue(Number(data.total ?? 0), 'emissions').toFixed(2))}
                                    />
                                </td>
                                <td className="cee_value" style={{ verticalAlign: "middle" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                                    <span>{displayEmissionsUnit()}</span>
                                        <ButtonGroup vertical size="sm">
                                            <ToggleButton
                                                id="emissions-toggle-metric"
                                                type="checkbox"
                                                variant={emissionsUnit === 'kgCO2eq_m3' ? 'primary' : 'outline-primary'}
                                                checked={emissionsUnit === 'kgCO2eq_m3'}
                                                onChange={toggleEmissionsUnit}
                                                disabled={!isStateSelected}
                                            >
                                                {unitSystem === 'metric' ? 'kg CO₂eq/m³' : 'lb CO₂eq/yd³'}
                                            </ToggleButton>

                                            <ToggleButton
                                                id="emissions-toggle-imperial"
                                                type="checkbox"
                                                variant={emissionsUnit === 'kgCO2eq_yd3' ? 'primary' : 'outline-primary'}
                                                checked={emissionsUnit === 'kgCO2eq_yd3'}
                                                onChange={toggleEmissionsUnit}
                                                disabled={!isStateSelected}
                                            >
                                                kg CO₂eq/yd³
                                            </ToggleButton>
                                        </ButtonGroup>

                                    </div>
                                </td>
                            </tr>


                            {/*<tr>*/}
                            {/*    <td>Total Cost</td>*/}
                            {/*    <td>*/}
                            {/*        <Form.Control*/}
                            {/*            type="number"*/}
                            {/*            size="sm"*/}
                            {/*            step="0.0001"*/}
                            {/*            style={{ width: "95%" }}*/}
                            {/*            name="total"*/}
                            {/*            id="total"*/}
                            {/*            value={parseFloat(displayValue(Number(data.total ?? 0), 'cost').toFixed(4))}*/}
                            {/*        />*/}
                            {/*    </td>*/}
                            {/*    <td className="cee_value" style={{ textAlign: "right" }}>*/}
                            {/*        {displayUnit('$/m³', '$/yd³')}*/}
                            {/*    </td>*/}
                            {/*</tr>*/}
                        </table>
                    </div>
                    </div>
                </Col>

            </Row>
            <Row>
                <Col>
                    <div className="pcb__box-footer-left">
                        <Button variant="secondary" onClick={goBack}>Cancel</Button>
                    </div>
                </Col>
                <Col>
                    <div className="pcb__box-footer-right">
                        <Button variant="primary" disabled={!isStateSelected} onClick={handleSubmit}>Save and Next</Button>
                    </div>
                </Col>
            </Row>

            <Modal show={errorPopupShow} onHide={handleErrorPopupClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>{error}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleErrorPopupClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={stateWarningShow} onHide={() => setStateWarningShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>State Required</Modal.Title>
                </Modal.Header>
                <Modal.Body>Please select a state before continuing.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setStateWarningShow(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={validationErrorShow} onHide={() => setValidationErrorShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Validation Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>Please fix the highlighted errors before continuing. All fields are required and number values must be greater than or equal to 0.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setValidationErrorShow(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
        </>
    );
};

export default ConcreteEmission;
